// frontend/src/components/ServiceReviewCarousel.tsx
import React, { useState, useEffect } from 'react';
import { Star, ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';
const UPLOADS_BASE = process.env.REACT_APP_API_BASE_URL?.replace('/api', '') || 'http://localhost:5000';

const serviceLabels: Record<string, string> = {
  'svincolo-atti': 'Svincolo Atti',
  'voucher-rimborso': 'Voucher Rimborso',
  'collabora': 'Collabora'
};

interface FeaturedReview {
  _id: string;
  associateSnapshot: {
    nominativo: string;
    ragioneSociale: string;
    citta: string;
    provincia: string;
    foto: string;
  };
  serviceType: string;
  rating: number;
  comment: string;
  tags: string[];
  publishedAt: string;
}

const ServiceReviewCarousel: React.FC = () => {
  const [reviews, setReviews] = useState<FeaturedReview[]>([]);
  const [globalAvg, setGlobalAvg] = useState(0);
  const [globalTotal, setGlobalTotal] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    const fetchFeatured = async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/public/service-reviews/featured?limit=10`);
        setReviews(data.reviews || []);
        setGlobalAvg(data.globalAvgRating || 0);
        setGlobalTotal(data.globalTotal || 0);
      } catch { /* silenzioso */ }
      finally { setLoading(false); }
    };
    fetchFeatured();
  }, []);

  // Auto-scroll ogni 5 secondi, si ferma al hover
  useEffect(() => {
    if (paused || reviews.length <= 3) return;
    const max = Math.max(0, reviews.length - 3);
    const timer = setInterval(() => {
      setCurrentIndex(i => i >= max ? 0 : i + 1);
    }, 5000);
    return () => clearInterval(timer);
  }, [paused, reviews.length]);

  if (loading || reviews.length === 0) return null;

  const visibleCount = 3;
  const maxIndex = Math.max(0, reviews.length - visibleCount);

  return (
    <section className="py-12 bg-gray-50 dark:bg-gray-800/50">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <h2 className="text-base font-bold text-gray-900 dark:text-white mb-2">
            Cosa dicono i nostri associati
          </h2>
          {globalTotal > 0 && (
            <div className="flex items-center justify-center gap-2">
              <div className="flex gap-0.5">
                {[1, 2, 3, 4, 5].map(s => (
                  <Star key={s} className={`w-4 h-4 ${s <= Math.round(globalAvg) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} />
                ))}
              </div>
              <span className="text-sm font-semibold text-gray-900 dark:text-white">{globalAvg.toFixed(1)}</span>
              <span className="text-sm text-gray-500">su 5 — {globalTotal} recensioni</span>
            </div>
          )}
        </div>

        {/* Carousel */}
        <div className="relative" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
          {reviews.length > visibleCount && (
            <>
              <button
                onClick={() => setCurrentIndex(i => Math.max(0, i - 1))}
                disabled={currentIndex === 0}
                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 p-2 bg-white dark:bg-gray-700 rounded-full shadow-lg disabled:opacity-30 hover:bg-gray-50 dark:hover:bg-gray-600"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => setCurrentIndex(i => Math.min(maxIndex, i + 1))}
                disabled={currentIndex >= maxIndex}
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 p-2 bg-white dark:bg-gray-700 rounded-full shadow-lg disabled:opacity-30 hover:bg-gray-50 dark:hover:bg-gray-600"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </>
          )}

          <div className="overflow-hidden">
            <div
              className="flex transition-transform duration-300 gap-4"
              style={{ transform: `translateX(-${currentIndex * (100 / visibleCount + 1.33)}%)` }}
            >
              {reviews.map(review => (
                <div
                  key={review._id}
                  className="flex-shrink-0 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm"
                  style={{ width: `calc(${100 / visibleCount}% - 1rem)` }}
                >
                  <div className="flex items-center gap-3 mb-3">
                    {review.associateSnapshot?.foto ? (
                      <img src={`${UPLOADS_BASE}${review.associateSnapshot.foto}`} alt="" className="w-9 h-9 rounded-full object-cover" />
                    ) : (
                      <div className="w-9 h-9 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center text-sm font-medium text-blue-600 dark:text-blue-300">
                        {(review.associateSnapshot?.nominativo || '?')[0]}
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {review.associateSnapshot?.ragioneSociale || review.associateSnapshot?.nominativo}
                      </p>
                      <p className="text-2xs text-gray-500">{review.associateSnapshot?.citta}</p>
                    </div>
                  </div>
                  <div className="flex gap-0.5 mb-2">
                    {[1, 2, 3, 4, 5].map(s => (
                      <Star key={s} className={`w-3.5 h-3.5 ${s <= review.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} />
                    ))}
                  </div>
                  {review.tags?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-2">
                      {review.tags.slice(0, 3).map(tag => (
                        <span key={tag} className="px-1.5 py-0.5 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded text-2xs capitalize">{tag}</span>
                      ))}
                    </div>
                  )}
                  {review.comment && (
                    <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-3">{review.comment}</p>
                  )}
                  <p className="text-2xs text-gray-400 mt-2">
                    {serviceLabels[review.serviceType]} — {new Date(review.publishedAt).toLocaleDateString('it-IT')}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Link */}
        <div className="text-center mt-6">
          <Link
            to="/rete-certificata/recensioni"
            className="inline-flex items-center gap-1 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium"
          >
            Vedi tutte le recensioni
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ServiceReviewCarousel;
